<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'

import { useApiTabStore } from '@/stores/apiTab.store'

import BaseTextbox from '@/components/forms/BaseTextbox.vue'
import { BODY_RAW_TYPES, BODY_TYPES } from '@/utils/apiTab'
import { RequestSection } from '@/utils/apiTab'

const props = defineProps({
  activeRequestIndex: {
    type: Number,
    required: true
  },
  bodyRawContentType: {
    type: String,
    required: true
  },
  handleUpdateRawContentType: {
    type: Function,
    required: true
  }
})

const apiTabStore = useApiTabStore()

const rawBodyPayloads = ref<{
  rawValue: string
  rawContentType?: string
}>({
  rawValue: ''
})
/**
 * Retrieves raw body data from the store based on the active request index.
 * If valid raw body data is found, it updates the raw content type and
 * the raw value in the `rawBodyPayloads` object. If no valid data is found,
 * it sets default values for the raw content type and raw value.
 */
const retrieveValuesFromStore = () => {
  const bodyRawData = apiTabStore.getRequestFieldData(
    props.activeRequestIndex,
    RequestSection.BODY,
    BODY_TYPES.RAW_DATA
  )
  if (
    bodyRawData &&
    typeof bodyRawData === 'object' &&
    'rawContentType' in bodyRawData &&
    'rawValue' in bodyRawData
  ) {
    props.handleUpdateRawContentType(bodyRawData.rawContentType)
    rawBodyPayloads.value.rawValue = bodyRawData.rawValue || ''
  } else {
    props.handleUpdateRawContentType(BODY_RAW_TYPES.TEXT)
    rawBodyPayloads.value.rawValue = ''
  }
}

onMounted(() => {
  retrieveValuesFromStore()
})

watch(
  () => props.activeRequestIndex,
  () => {
    retrieveValuesFromStore()
  }
)

watch(
  () => [rawBodyPayloads.value, props.bodyRawContentType],
  () => {
    let bodyPayloadData = {
      rawContentType: props.bodyRawContentType,
      rawValue: rawBodyPayloads.value.rawValue
    }

    apiTabStore.updateRequestField(
      props.activeRequestIndex,
      RequestSection.BODY,
      bodyPayloadData,
      BODY_TYPES.RAW_DATA
    )
  },
  { deep: true }
)
</script>

<template>
  <div class="m-0 mx-auto w-full max-w-full">
    <BaseTextbox
      :rows="8"
      v-model="rawBodyPayloads.rawValue"
      isCustomClass="outline-none background-primary !border-border-primary"
      :isLightGray="false"
    />
  </div>
</template>
